import { useLingui } from '@lingui/react/macro';

import { StatusBadge } from '@/shared/ui/badge';
import { cn } from '@/shared/ui/cn';

import { PRESET_META, type ColumnSpec } from './columns';
import { STATUS_ICONS, type StatusEntry } from './status';
import { readPath, resolvePatternText } from './text';
import { formatScalar, isBlank, type ScalarFormat } from './values';

function asText(value: unknown): string {
  return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' ? String(value) : JSON.stringify(value);
}

/** Entrada do mapa só se for dele: valor como `constructor` não acha o protótipo do objeto. */
function ownEntry<T>(map: Readonly<Record<string, T>> | undefined, key: string): T | undefined {
  return map && Object.hasOwn(map, key) ? map[key] : undefined;
}

/** "N/D" do legado (131 colunas aplicavam à mão), num lugar só. */
export function NotAvailable({ className }: { readonly className?: string | undefined }) {
  const { t } = useLingui();
  return <span className={cn('text-muted-foreground', className)}>{t`N/D`}</span>;
}

/** Status pelo valor técnico, com tom e ícone (U19). Valor fora do mapa: neutro, com o rótulo da API. */
export function StatusValue({ entry, value, fallbackLabel }: { readonly entry: StatusEntry | undefined; readonly value: string; readonly fallbackLabel?: string | undefined }) {
  const { i18n } = useLingui();
  if (!entry) return <StatusBadge tone="neutral" label={fallbackLabel ?? value} />;
  return <StatusBadge tone={entry.tone} label={resolvePatternText(i18n, entry.label)} {...(entry.icon ? { icon: STATUS_ICONS[entry.icon] } : {})} />;
}

/** Valor de uma coluna para uma linha, no formato do preset. Serve à tabela, ao cartão e ao detalhe. */
export function ColumnValue({ column, row, className }: { readonly column: ColumnSpec; readonly row: unknown; readonly className?: string }) {
  const hint = column.hintField ? readPath(row, column.hintField) : undefined;
  if (typeof hint === 'string' && hint !== '') {
    const { hintField: _hintField, ...plain } = column;
    return <span title={hint}><ColumnValue column={plain} row={row} {...(className ? { className } : {})} /></span>;
  }
  return <PresetColumnValue column={column} row={row} {...(className ? { className } : {})} />;
}

function PresetColumnValue({ column, row, className }: { readonly column: ColumnSpec; readonly row: unknown; readonly className?: string }) {
  const { i18n, t } = useLingui();
  const value = readPath(row, column.field);
  const options = column.options ?? {};
  const mono = PRESET_META[column.preset].mono;
  const fallback = options.fallbackField ? readPath(row, options.fallbackField) : undefined;
  const fallbackLabel = typeof fallback === 'string' && fallback.trim() !== '' ? fallback : undefined;
  if (isBlank(value)) {
    // O legado lia o `*Display` direto: sem o valor técnico, o rótulo da API ainda aparece.
    if (fallbackLabel && column.preset === 'status') return <StatusValue entry={undefined} value="" fallbackLabel={fallbackLabel} />;
    if (fallbackLabel && column.preset === 'enum') return <span className={className}>{fallbackLabel}</span>;
    return <NotAvailable className={className} />;
  }

  if (column.preset === 'status') {
    const key = asText(value);
    return <StatusValue entry={ownEntry(options.statuses, key)} value={key} fallbackLabel={fallbackLabel} />;
  }
  if (column.preset === 'enum') {
    const label = ownEntry(options.values, asText(value));
    return <span className={className}>{label ? resolvePatternText(i18n, label) : (fallbackLabel ?? asText(value))}</span>;
  }
  if (column.preset === 'bool') {
    return <span className={className}>{value === true ? t`Sim` : value === false ? t`Não` : asText(value)}</span>;
  }

  const formatted = formatScalar(column.preset satisfies ScalarFormat, value, i18n.locale, {
    ...(options.unit ? { unit: options.unit } : {}),
    ...(options.document ? { document: options.document } : {}),
    ...(options.maxFractionDigits === undefined ? {} : { maxFractionDigits: options.maxFractionDigits }),
  });
  // Fora do formato (documento inválido, data torta): mostra o cru, não esconde (D10).
  const text = formatted ?? (typeof value === 'string' || typeof value === 'number' ? String(value) : null);
  if (text === null) return <NotAvailable className={className} />;
  const title = column.preset === 'date' || column.preset === 'datetime' ? formatScalar('datetime', value, i18n.locale) ?? undefined : undefined;
  return (
    <span className={cn(mono && 'font-mono tabular-nums', className)} {...(title ? { title } : {})}>
      {text}
    </span>
  );
}
